// ABOUTME: Email change cancellation page that cancels pending email change requests
// ABOUTME: Handles token-based cancellation when user clicks cancel link from email

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Container, Typography, Paper, Box, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import api from '../services/api';

export default function CancelEmailChange() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  // Auto-trigger cancellation on mount when token exists
  useEffect(() => {
    if (token) {
      handleCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCancel intentionally omitted; it is stable and adding it would cause extra invocations
  }, [token]);

  const handleCancel = async () => {
    if (!token) return;

    setCancelling(true);
    setError(null);
    try {
      const response = await api.post('/api/user/cancel-email-change', { token });

      if (response.status === 200) {
        setCancelled(true);
        // Redirect to profile after 3 seconds
        setTimeout(() => {
          navigate('/my-profile?message=Email change request cancelled successfully.');
        }, 3000);
      }
    } catch (err: unknown) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to cancel email change. The request may have already been completed or expired.');
    } finally {
      setCancelling(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>Invalid Link</Typography>
          <Typography>This cancellation link is invalid.</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>Cancel Email Change</Typography>

        {cancelled ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your email change request has been cancelled successfully!
            </Alert>
            <Typography sx={{ mb: 2 }}>
              You will be redirected to your profile...
            </Typography>
            <Box display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          </Box>
        ) : error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/my-profile')}>
              Go to My Profile
            </Button>
          </>
        ) : cancelling ? (
          <Box>
            <Typography sx={{ mb: 3, textAlign: 'center' }}>
              Cancelling your email change request...
            </Typography>
            <Box display="flex" justifyContent="center">
              <CircularProgress size={40} />
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Container>
  );
}
